import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';

function PixelListPage({ contract }) {
  const [mintedGroups, setMintedGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchMintedGroups = async () => {
      if (!contract) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        // Get NFTGroupMinted events
        const groupEvents = await contract.queryFilter('NFTGroupMinted');
        
        // Process events
        const processedGroups = [];
        
        for (const event of groupEvents) {
          try {
            const startPosition = parseInt(event.args.startPosition.toString());
            const size = parseInt(event.args.size.toString());
            const endPosition = startPosition + (size * size) - 1;
            
            // Get timestamp from block
            const block = await event.getBlock();
            const timestamp = new Date(block.timestamp * 1000);
            
            // Calculate price
            const pixelCount = size * size;
            const pricePerPixel = 0.00001; // Same as in contract
            const price = pixelCount * pricePerPixel;
            
            // Get metadata
              let description = '';
              let imageUrl = '';
              let website = '';
              try {
                  const uri = await contract.tokenURI(startPosition);
                  if (uri && uri.startsWith('data:application/json;base64,')) {
                      const json = atob(uri.split(',')[1]);
                      const metadata = JSON.parse(json);
                      description = metadata.description || '';
                      imageUrl = metadata.image || '';
                      website = metadata.website || '';
                  }
              } catch (err) {
                  console.error(`Error getting metadata for position ${startPosition}:`, err);
              }

              processedGroups.push({
                  startPosition,
                  endPosition,
                  size,
                  pixelCount,
                  price,
                  timestamp,
                  description,
                  imageUrl,
                  website
              });
          } catch (err) {
            console.error('Error processing NFTGroupMinted event:', err);
          }
        }
        
        // Sort by timestamp (newest first)
        processedGroups.sort((a, b) => b.timestamp - a.timestamp);
        
        setMintedGroups(processedGroups);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching minted groups:', err);
        setError('Error loading pixel list: ' + err.message);
        setIsLoading(false);
      }
    };

    fetchMintedGroups();
  }, [contract]);

  // Format date as YYYY-MM-DD
  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  if (!contract) {
    return (
      <div style={{
        maxWidth: '600px',
        margin: '60px auto',
        padding: '32px',
        background: 'linear-gradient(90deg, #e8f5e9 0%, #e3f2fd 100%)',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(44, 124, 90, 0.08)',
        textAlign: 'center',
        color: '#2E7D32',
        fontSize: '1.15rem',
        fontWeight: 500
      }}>
        <span role="img" aria-label="wave" style={{ fontSize: '2rem', marginBottom: '10px', display: 'block' }}>👋</span>
        <div style={{ marginBottom: '8px' }}>Welcome to the Pixel List!</div>
        <div style={{ color: '#1565C0', fontWeight: 400 }}>
          Please connect with your wallet to view the list of purchased pixels.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1000px',
      margin: '0 auto',
      padding: '20px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }}>
      <h2 style={{ color: '#2E7D32', marginBottom: '20px' }}>Pixel Purchase List</h2>
      
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Loading pixel list...
        </div>
      ) : error ? (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#FFEBEE',
          color: '#C62828',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ 
  width: '100%', 
  borderCollapse: 'collapse',
  fontSize: '14px'
}}>
  <thead>
    <tr style={{ 
      backgroundColor: '#f5f5f5', 
      borderBottom: '2px solid #ddd'
    }}>
      <th style={{ padding: '12px 15px', textAlign: 'left' }}>Date</th>
      <th style={{ padding: '12px 15px', textAlign: 'left' }}>Pixels</th>
      <th style={{ padding: '12px 15px', textAlign: 'left' }}>Size</th>
      <th style={{ padding: '12px 15px', textAlign: 'left' }}>Price (ETH)</th>
      <th style={{ padding: '12px 15px', textAlign: 'left' }}>Website</th>
      <th style={{ padding: '12px 15px', textAlign: 'left' }}>Description</th>
      <th style={{ padding: '12px 15px', textAlign: 'left' }}>Image</th>
    </tr>
  </thead>
  <tbody>
    {mintedGroups.map((group, index) => (
      <tr key={index} style={{ 
        borderBottom: '1px solid #ddd',
        backgroundColor: index % 2 === 0 ? 'white' : '#f9f9f9'
      }}>
        <td style={{ padding: '12px 15px' }}>{formatDate(group.timestamp)}</td>
        <td style={{ padding: '12px 15px' }}>#{group.startPosition}-{group.endPosition}</td>
        <td style={{ padding: '12px 15px' }}>{group.size}×{group.size} ({group.pixelCount})</td>
        <td style={{ padding: '12px 15px' }}>{group.price.toFixed(6)}</td>
        <td style={{ padding: '12px 15px' }}>
          {group.website && (
            <a 
              href={group.website.startsWith('http') ? group.website : `https://${group.website}`} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#2196F3', textDecoration: 'none' }}
            >
              {group.website.replace(/^https?:\/\//, '')}
            </a>
          )}
        </td>
        <td style={{ padding: '12px 15px' }}>{group.description}</td>
        <td style={{ padding: '12px 15px' }}>
          {group.imageUrl && (
            <img 
              src={group.imageUrl} 
              alt="Pixel preview" 
              style={{ 
                width: '40px', 
                height: '40px', 
                objectFit: 'cover',
                border: '1px solid #ddd'
              }} 
            />
          )}
        </td>
      </tr>
    ))}
  </tbody>
</table>
        </div>
      )}
    </div>
  );
}

export default PixelListPage;